<script>
  import { onDestroy, onMount } from 'svelte';
  import { getApiBase } from './apiBase.js';
  import { SUBJECT_NAMES } from './subjectCatalog.js';
  import { loadTeacherWorkQrPreview } from './teacherWorkQrApi.js';
  import AdminTeacherPersonalNotes from './AdminTeacherPersonalNotes.svelte';

  export let embedded = false;
  export let teacherId = null;
  export let teacher = null; // optional: passed from dashboard to avoid waiting on teacher list
  const apiBase = getApiBase();

  let teachers = [];
  let selectedTeacher = null;
  let teacherDetails = null;
  let teacherOverview = null;
  let teacherComments = null;
  let loading = false;
  let loadingOverview = false;
  let loadingComments = false;
  let error = '';
  let editMode = false;
  let saving = false;
  let teacherQuery = '';
  let filteredTeachers = [];
  let showTeacherDropdown = false;
  let embeddedSelecting = false;
  let qrCodePreviewUrl = '';
  let qrCodePdfUrl = '';
  let qrCodeLoading = false;
  let qrCodeError = '';

  // Month selection
  let selectedMonth = new Date().getMonth() + 1; // 1-12
  let selectedYear = new Date().getFullYear();
  let availableYears = [];

  // Editable teacher data
  let editableData = {
    teacher: {},
    teacher_info: {},
    timeslots: []
  };

  // Available subjects list (will be populated from backend)
  let availableSubjects = [];
  
  // Subject editing functions
  function toggleSubject(subject) {
    if (editableData.teacher_info.subjects.includes(subject)) {
      editableData.teacher_info.subjects = editableData.teacher_info.subjects.filter(s => s !== subject);
    } else {
      editableData.teacher_info.subjects = [...editableData.teacher_info.subjects, subject];
    }
    
    // Force reactive update by reassigning the object
    editableData = editableData;
  }

  function removeSubject(subject) {
    editableData.teacher_info.subjects = editableData.teacher_info.subjects.filter(s => s !== subject);
    // Force reactive update by reassigning the object
    editableData = editableData;
  }

  onMount(async () => {
    await loadTeachers();
    await loadAvailableSubjects();
    initializeAvailableYears();
  });

  // Embedded mode: if dashboard passed a teacher object, use it immediately.
  // This prevents getting stuck on "Loading teacher details..." while waiting for teacher lists.
  $: if (embedded && teacher?.id && (!selectedTeacher || selectedTeacher.id !== teacher.id) && !embeddedSelecting) {
    embeddedSelecting = true;
    Promise.resolve(selectTeacher(teacher)).finally(() => {
      embeddedSelecting = false;
    });
  }

  function initializeAvailableYears() {
    const currentYear = new Date().getFullYear();
    // Show current year and previous 2 years
    availableYears = [currentYear, currentYear - 1, currentYear - 2];
  }

  async function loadTeachers() {
    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${apiBase}/studentplayground/api/teachers-list/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.status === 'success') {
          teachers = data.teachers || [];
          filteredTeachers = teachers;
        } else {
          throw new Error(data.message || 'Failed to load teachers');
        }
      } else {
        if (response.status === 401) {
          // If response is 401 Unauthorized, redirect to login
          alert('Reikia prisijungti iš naujo.');
          localStorage.removeItem('jwtToken');
          window.location.href = '#/about';
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      }
    } catch (err) {
      console.error('Error loading teachers:', err);
      error = `Error loading teachers: ${err.message}`;
    }
  }

  async function loadAvailableSubjects() {
    availableSubjects = SUBJECT_NAMES;
  }

  async function selectTeacher(teacher) {
    closeQrCodePreview();
    selectedTeacher = teacher;
    teacherQuery = teacher.name;
    showTeacherDropdown = false; // Explicitly close dropdown
    teacherDetails = null;
    teacherOverview = null;
    teacherComments = null;
    editMode = false;
    loading = true;
    error = '';
    
    // Force update to ensure dropdown closes
    filteredTeachers = teachers;

    await loadTeacherData();
  }

  async function viewTeacherQrCode() {
    if (!selectedTeacher?.id) return;
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      qrCodeError = 'Please sign in again to view the QR code.';
      return;
    }

    qrCodeLoading = true;
    qrCodeError = '';
    closeQrCodePreview();
    try {
      const preview = await loadTeacherWorkQrPreview({
        apiBase,
        token,
        contentType: teacherDetails.teacher.work_with_children_qr_code?.content_type,
        teacherId: selectedTeacher.id,
        fallbackError: 'Failed to load QR code.'
      });
      if (preview.kind === 'pdf') {
        qrCodePdfUrl = preview.objectUrl;
      } else {
        qrCodePreviewUrl = preview.objectUrl;
      }
    } catch (err) {
      qrCodeError = err.message || 'Failed to load QR code.';
    } finally {
      qrCodeLoading = false;
    }
  }

  function closeQrCodePreview() {
    if (qrCodePreviewUrl) URL.revokeObjectURL(qrCodePreviewUrl);
    if (qrCodePdfUrl) URL.revokeObjectURL(qrCodePdfUrl);
    qrCodePreviewUrl = '';
    qrCodePdfUrl = '';
    qrCodeError = '';
  }

  onDestroy(closeQrCodePreview);

  async function loadTeacherData() {
    if (!selectedTeacher) return;
    
    loading = true;
    error = '';
    
    // Clear old data to force refresh
    teacherOverview = null;
    teacherComments = null;

    try {
      const token = localStorage.getItem('jwtToken');
      
      // Build query parameters for month/year
      const monthParam = `?month=${selectedMonth}&year=${selectedYear}`;
      
      console.log(`Loading teacher data for ${getMonthName(selectedMonth)} ${selectedYear}`, monthParam);
      
      // Fetch detailed info, overview, and comments in parallel
      const [detailsResponse, overviewResponse, commentsResponse] = await Promise.all([
        fetch(`${apiBase}/studentplayground/api/teacher-detailed-info/${selectedTeacher.id}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${apiBase}/studentplayground/api/teacher-overview/${selectedTeacher.id}${monthParam}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch(`${apiBase}/studentplayground/api/teacher_performance_rating/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            teacher_id: selectedTeacher.id,
            teacher_name: selectedTeacher.name,
            selected_at: new Date().toISOString(),
            selected_month: `${selectedYear}-${String(selectedMonth).padStart(2, '0')}`
          })
        })
      ]);

      if (detailsResponse.ok) {
        const data = await detailsResponse.json();
        if (data.status === 'success') {
          teacherDetails = data;
          // Initialize editable data
          initializeEditableData(data);
        } else {
          throw new Error(data.message || 'Failed to load teacher details');
        }
      } else {
        if (detailsResponse.status === 401) {
          // If response is 401 Unauthorized, redirect to login
          alert('Reikia prisijungti iš naujo.');
          localStorage.removeItem('jwtToken');
          window.location.href = '#/about';
        } else {
          throw new Error(`HTTP error! status: ${detailsResponse.status}`);
        }
      }

      if (overviewResponse.ok) {
        const overviewData = await overviewResponse.json();
        if (overviewData.status === 'success') {
          console.log('Teacher overview data received:', {
            total_classes: overviewData.current_month?.total_classes,
            cancelled_classes: overviewData.current_month?.cancelled_classes,
            students_count: overviewData.students?.length,
            all_week_classes_count: overviewData.all_week_classes?.length
          });
          teacherOverview = overviewData;
        } else {
          console.warn('Failed to load teacher overview:', overviewData.message);
        }
      } else {
        console.warn('Failed to fetch teacher overview');
      }

      if (commentsResponse.ok) {
        const commentsData = await commentsResponse.json();
        if (commentsData.status === 'success') {
          console.log('Teacher comments data received:', {
            total_comments: commentsData.comments_analysis?.total_comments
          });
          teacherComments = commentsData.comments_analysis;
        } else {
          console.warn('Failed to load teacher comments:', commentsData.error);
        }
      } else {
        console.warn('Failed to fetch teacher comments');
      }

    } catch (err) {
      console.error('Error loading teacher data:', err);
      error = `Error loading teacher data: ${err.message}`;
    } finally {
      loading = false;
    }
  }

  // Filter teachers based on search query
  function filterTeachers() {
    if (!teacherQuery.trim()) {
      filteredTeachers = teachers;
      showTeacherDropdown = false;
    } else {
      const query = teacherQuery.toLowerCase();
      filteredTeachers = teachers.filter(teacher => 
        teacher.name.toLowerCase().includes(query) ||
        teacher.email.toLowerCase().includes(query)
      );
      // Only show dropdown if no teacher is selected and there are filtered results
      showTeacherDropdown = !selectedTeacher && filteredTeachers.length > 0;
    }
  }

  // Handle month/year change
  async function handleMonthChange() {
    if (selectedTeacher) {
      await loadTeacherData();
    }
  }

  // Get month name for display
  function getMonthName(monthNum) {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return months[monthNum - 1];
  }

  // Initialize editable data from teacher details
  function initializeEditableData(data) {
    // Use legacy format for backward compatibility in editing
    const legacyInfo = data.teacher_info_legacy || data.teacher_info;
    
    // Ensure subjects is always an array
    let subjects = [];
    if (Array.isArray(legacyInfo?.subjects)) {
      subjects = [...legacyInfo.subjects];
    } else if (Array.isArray(legacyInfo) && legacyInfo.length > 0 && Array.isArray(legacyInfo[0]?.subjects)) {
      // If teacher_info is an array, get subjects from first element
      subjects = [...legacyInfo[0].subjects];
    }
    
    
    editableData = {
      teacher: { 
        ...data.teacher,
        meeting_link: data.teacher.meeting_link || ''
      },
      teacher_info: { 
        subjects: subjects,
        grade: legacyInfo?.grade || (Array.isArray(legacyInfo) && legacyInfo[0]?.grade) || ''
      },
      timeslots: data.timeslots.map(slot => ({ ...slot }))
    };
  }

  // Toggle edit mode
  function toggleEditMode() {
    if (editMode) {
      // Reset editable data to original
      if (teacherDetails) {
        initializeEditableData(teacherDetails);
      }
    }
    editMode = !editMode;
    error = '';
  }

  // Add new time slot
  function addTimeSlot() {
    editableData.timeslots = [...editableData.timeslots, {
      id: null,
      day_of_week: 1,
      day_name: 'Monday',
      start_time: '09:00',
      end_time: '10:00'
    }];
  }

  // Remove time slot
  function removeTimeSlot(index) {
    editableData.timeslots = editableData.timeslots.filter((_, i) => i !== index);
  }

  // Update day name when day of week changes
  function updateDayName(index, dayOfWeek) {
    const dayMapping = {
      1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 
      4: 'Thursday', 5: 'Friday', 6: 'Saturday', 7: 'Sunday'
    };
    editableData.timeslots[index].day_of_week = parseInt(dayOfWeek);
    editableData.timeslots[index].day_name = dayMapping[dayOfWeek];
  }

  // Save teacher information
  async function saveTeacherInfo() {
    if (!selectedTeacher) {
      error = 'No teacher selected';
      return;
    }

    saving = true;
    error = '';

    try {
      const token = localStorage.getItem('jwtToken');
      const response = await fetch(`${apiBase}/studentplayground/api/update-teacher-detailed-info/${selectedTeacher.id}/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(editableData)
      });
      const data = await response.json();

      if (response.ok) {
        if (data.status === 'success') {
          // Update the displayed data with the saved data
          teacherDetails = data;
          initializeEditableData(data);
          editMode = false;
          alert('Teacher information updated successfully!');
        } else {
          throw new Error(data.message || 'Failed to save teacher information');
        }
      } else {
        if (response.status === 401) {
          alert('Reikia prisijungti iš naujo.');
          localStorage.removeItem('jwtToken');
          window.location.href = '#/about';
        } else {
          throw new Error(data.indiv_lic || data.message || `HTTP error! status: ${response.status}`);
        }
      }
    } catch (err) {
      console.error('Error saving teacher information:', err);
      error = `Error saving teacher information: ${err.message}`;
    } finally {
      saving = false;
    }
  }

  function formatTimeslots(timeslots) {
    if (!timeslots || timeslots.length === 0) {
      return 'No time slots defined';
    }
    
    return timeslots.map(slot => 
      `${slot.day_name}: ${slot.start_time} - ${slot.end_time}`
    ).join(', ');
  }

  // Reactive statement to filter teachers when search query changes
  $: if (teacherQuery !== undefined) {
    filterTeachers();
  }
  
  // Reactive statement to force UI updates when subjects change
  $: editableSubjects = editableData.teacher_info.subjects;

  // Embedded mode: auto-select teacher by id and hide internal search UI
  $: if (embedded && teacherId && teachers.length && !embeddedSelecting) {
    const match = teachers.find(t => t.id === Number(teacherId));
    if (match && (!selectedTeacher || selectedTeacher.id !== match.id)) {
      embeddedSelecting = true;
      Promise.resolve(selectTeacher(match)).finally(() => {
        embeddedSelecting = false;
      });
    }
  }
</script>

<div class="teacher-info-container" class:embedded={embedded}>
  <h3>{embedded ? 'Teacher Information' : 'Teacher Information Lookup'}</h3>
  
  {#if error}
    <div class="error-message">
      {error}
    </div>
  {/if}

  <!-- Teacher Selection -->
  {#if !embedded}
    <div class="selection-section">
      <h4>Step 1: Select Teacher</h4>
      
      <div class="input-container">
        <input 
          type="text" 
          placeholder="Search teacher by name..."
          bind:value={teacherQuery}
          on:input={() => {
            if (selectedTeacher && teacherQuery !== selectedTeacher.name) {
              selectedTeacher = null;
              showTeacherDropdown = true;
            } else if (!selectedTeacher) {
              showTeacherDropdown = teacherQuery.trim().length > 0;
            }
          }}
          autocomplete="off"
          class="search-input"
        />
        
        {#if showTeacherDropdown}
          <ul class="autocomplete-list">
            {#each filteredTeachers.slice(0, 8) as teacher}
              <li>
                <button
                  type="button"
                  on:click={() => selectTeacher(teacher)}
                  on:keydown={(e) => (e.key === 'Enter' || e.key === ' ') && selectTeacher(teacher)}
                >
                  <div class="teacher-option">
                    <span class="teacher-name">{teacher.name}</span>
                    {#if teacher.email}
                      <span class="teacher-email">{teacher.email}</span>
                    {/if}
                  </div>
                </button>
              </li>
            {/each}
          </ul>
        {/if}
      </div>
      
      {#if selectedTeacher}
        <div class="selected-teacher">
          <div class="selected-teacher-info">
            <h5>Selected Teacher</h5>
            <p><strong>Name:</strong> {selectedTeacher.name}</p>
            {#if selectedTeacher.email}
              <p><strong>Email:</strong> {selectedTeacher.email}</p>
            {/if}
            <p><strong>Classes:</strong> {selectedTeacher.class_count}</p>
          </div>
          
          <button 
            type="button" 
            class="btn-clear-small" 
            on:click={() => { 
              selectedTeacher = null; 
              teacherQuery = ''; 
              showTeacherDropdown = false; 
              teacherDetails = null;
              teacherOverview = null;
              teacherComments = null;
              editMode = false;
            }}
          >
            Clear Selection
          </button>
        </div>
      {/if}
    </div>
  {:else}
    {#if !selectedTeacher}
      <div class="loading">Loading teacher details...</div>
    {/if}
  {/if}

  <!-- Month Selection -->
  <div class="selection-section">
    <h4>{embedded ? 'Month & Year' : 'Step 2: Select Month & Year'}</h4>
    <div class="month-selection">
      <div class="month-selector-container">
        <label for="month-select" class="selector-label">Month:</label>
        <select 
          id="month-select"
          bind:value={selectedMonth} 
          on:change={handleMonthChange}
          class="month-select"
        >
          <option value={1}>January</option>
          <option value={2}>February</option>
          <option value={3}>March</option>
          <option value={4}>April</option>
          <option value={5}>May</option>
          <option value={6}>June</option>
          <option value={7}>July</option>
          <option value={8}>August</option>
          <option value={9}>September</option>
          <option value={10}>October</option>
          <option value={11}>November</option>
          <option value={12}>December</option>
        </select>
      </div>
      
      <div class="month-selector-container">
        <label for="year-select" class="selector-label">Year:</label>
        <select 
          id="year-select"
          bind:value={selectedYear} 
          on:change={handleMonthChange}
          class="year-select"
        >
          {#each availableYears as year}
            <option value={year}>{year}</option>
          {/each}
        </select>
      </div>

      <div class="selected-period">
        <span class="period-label">Viewing data for:</span>
        <span class="period-value">{getMonthName(selectedMonth)} {selectedYear}</span>
      </div>
    </div>
  </div>

  <!-- Loading Indicator -->
  {#if loading}
    <div class="loading">
      Loading teacher details...
    </div>
  {/if}

  {#if selectedTeacher}
    <AdminTeacherPersonalNotes teacherId={selectedTeacher.id} />
  {/if}

  <!-- Teacher Overview Display -->
  {#if teacherOverview && !loading}
    <div class="teacher-overview">
      <h4>Teacher Overview: {teacherOverview.teacher_name} - {getMonthName(selectedMonth)} {selectedYear}</h4>
      
      <!-- Classes This Month -->
      <div class="overview-section">
        <h5>📚 Classes for {getMonthName(selectedMonth)} {selectedYear}</h5>
        <div class="overview-grid">
          <div class="overview-item">
            <span class="overview-label">Total Classes:</span>
            <span class="overview-value">{teacherOverview.current_month.total_classes}</span>
          </div>
          <div class="overview-item">
            <span class="overview-label">Cancelled:</span>
            <span class="overview-value">{teacherOverview.current_month.cancelled_classes}</span>
          </div>
        </div>
      </div>

      <!-- Subjects Taught -->
      <div class="overview-section">
        <h5>📖 Subjects Taught</h5>
        <div class="subjects-overview">
          {#each Object.entries(teacherOverview.subjects_taught) as [subject, count]}
            <div class="subject-badge">
              <span class="subject-name">{subject}</span>
              <span class="subject-count">{count} classes</span>
            </div>
          {/each}
        </div>
      </div>

      <!-- Students -->
      <div class="overview-section">
        <h5>👥 Students ({teacherOverview.students.length} total)</h5>
        <div class="students-overview">
          {#each teacherOverview.students as student}
            <div class="student-item">
              <span class="student-name">{student.name}</span>
              <small class="student-details">{student.class_count} classes</small>
            </div>
          {/each}
        </div>
      </div>

      <!-- Teaching Start Date -->
      {#if teacherDetails && teacherDetails.teaching_preference}
        <div class="overview-section">
          <h5>📅 Teaching Start Date</h5>
          <div class="teaching-start-info">
            {#if teacherDetails.teaching_preference.preferred_start_date}
              <div class="start-date-item">
                <span class="start-date-label">Preferred Start Date:</span>
                <span class="start-date-value">{new Date(teacherDetails.teaching_preference.preferred_start_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            {:else}
              <p class="no-start-date">No preferred start date set</p>
            {/if}
          </div>
        </div>
      {/if}

      <!-- Available Time Slots -->
      <div class="overview-section">
        <h5>⏰ Available Time Slots This Week</h5>
        {#if teacherOverview.available_slots.length > 0}
          <div class="available-slots">
            {#each teacherOverview.available_slots as slot}
              <div class="slot-item available">
                <span class="slot-day">{slot.day_name}</span>
                <span class="slot-time">{slot.start_time} - {slot.end_time}</span>
                <span class="slot-status">Available</span>
              </div>
            {/each}
          </div>
        {:else}
          <p class="no-slots">No available time slots this week</p>
        {/if}
      </div>

      <!-- All Classes This Week -->
      <div class="overview-section">
        <h5>📅 All Classes This Week</h5>
        {#if teacherOverview.all_week_classes.length > 0}
          <div class="week-classes">
            {#each teacherOverview.all_week_classes as classItem}
              <div class="class-item {classItem.falls_in_timeslot ? 'in-timeslot' : 'outside-timeslot'}">
                <div class="class-time">
                  <span class="class-day">{classItem.day_name}</span>
                  <span class="class-datetime">{classItem.display_time}</span>
                </div>
                <div class="class-details">
                  <span class="class-name">{classItem.class_name}</span>
                  <span class="class-student">{classItem.student_name}</span>
                </div>
                {#if classItem.falls_in_timeslot}
                  <span class="timeslot-indicator">In Timeslot</span>
                {:else}
                  <span class="outside-indicator">Outside Timeslot</span>
                {/if}
              </div>
            {/each}
          </div>
        {:else}
          <p class="no-slots">No classes scheduled this week</p>
        {/if}
      </div>
    </div>
  {/if}

  <!-- Teacher Comments Analysis -->
  {#if teacherComments && !loading}
    <div class="teacher-comments">
      <h4>📊 Student Feedback Analysis - {getMonthName(selectedMonth)} {selectedYear}</h4>
      
      <div class="comments-summary">
        <div class="summary-header">
          <strong>Total Comments: {teacherComments.total_comments}</strong>
        </div>
        <p>Student feedback from {getMonthName(selectedMonth)} {selectedYear}</p>
      </div>

      {#if teacherComments.total_comments > 0}
        <!-- Rating Averages Section -->
        <div class="comments-section">
          <h5>⭐ Rating Averages (1-5 scale)</h5>
          <div class="month-comparison">
            <div class="month-data">
              <h6>Learning & Effort</h6>
              <ul>
                <li><strong>Effort Rating:</strong> {teacherComments.rating_averages.effort.average}/5 ({teacherComments.rating_averages.effort.rated_comments} ratings)</li>
                <li><strong>Learning Effectiveness:</strong> {teacherComments.rating_averages.learned.average}/5 ({teacherComments.rating_averages.learned.rated_comments} ratings)</li>
                <li><strong>General Satisfaction:</strong> {teacherComments.rating_averages.general.average}/5 ({teacherComments.rating_averages.general.rated_comments} ratings)</li>
              </ul>
            </div>
            
            <div class="month-data">
              <h6>Issues & Performance</h6>
              <ul>
                <li><strong>Technical Issues:</strong> {teacherComments.rating_averages.technical_issues.average}/5 ({teacherComments.rating_averages.technical_issues.rated_comments} reports)</li>
                <li><strong>Punctuality:</strong> {teacherComments.boolean_metrics.punctuality.punctuality_percentage}% on time</li>
                <li><strong>Class Completion:</strong> {teacherComments.boolean_metrics.class_completion.completion_percentage}% completed</li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Performance Metrics Section -->
        <div class="comments-section">
          <h5>📈 Performance Metrics</h5>
          <div class="metrics-grid">
            <div class="metric-item">
              <strong>Punctuality</strong>
              <div class="metric-bar">
                <div class="metric-fill" style="width: {teacherComments.boolean_metrics.punctuality.punctuality_percentage}%"></div>
              </div>
              <small>{teacherComments.boolean_metrics.punctuality.on_time_count} on time, {teacherComments.boolean_metrics.punctuality.late_count} late</small>
            </div>
            
            <div class="metric-item">
              <strong>Class Completion</strong>
              <div class="metric-bar">
                <div class="metric-fill" style="width: {teacherComments.boolean_metrics.class_completion.completion_percentage}%"></div>
              </div>
              <small>{teacherComments.boolean_metrics.class_completion.completed_count} completed, {teacherComments.boolean_metrics.class_completion.not_completed_count} not completed</small>
            </div>
          </div>
        </div>

        <!-- Sample Comments Section -->
        {#if teacherComments.sample_comments && teacherComments.sample_comments.length > 0}
          <div class="comments-section">
            <h5>💬 Recent Student Comments</h5>
            <div class="sample-comments">
              {#each teacherComments.sample_comments as comment}
                <div class="comment-item">
                  <strong>{comment.student}:</strong>
                  <span>"{comment.text}"</span>
                </div>
              {/each}
            </div>
          </div>
        {/if}
      {:else}
        <div class="no-data">
          <p>No student comments found for this teacher in the current month.</p>
        </div>
      {/if}
    </div>
  {/if}

  <!-- Teacher Details Display -->
  {#if teacherDetails && !loading}
    <div class="teacher-details">
      <div class="teacher-details-header">
        <h4>Teacher Details: {teacherDetails.teacher.name}</h4>
        <div class="edit-controls">
          <button class="btn btn-secondary" on:click={toggleEditMode}>
            {editMode ? 'Cancel Edit' : 'Edit Information'}
          </button>
          {#if editMode}
            <button class="btn btn-primary" on:click={saveTeacherInfo} disabled={saving}>
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          {/if}
        </div>
      </div>
      
      <!-- Basic Teacher Information -->
      <div class="info-section">
        <h5>Basic Information</h5>
        <div class="info-grid">
          <div class="info-item">
            <span class="label">Name:</span>
            {#if editMode}
              <input type="text" bind:value={editableData.teacher.name} class="edit-input" />
            {:else}
              <span class="value">{teacherDetails.teacher.name}</span>
            {/if}
          </div>
          <div class="info-item">
            <span class="label">Username:</span>
            {#if editMode}
              <input type="text" bind:value={editableData.teacher.username} class="edit-input" />
            {:else}
              <span class="value">{teacherDetails.teacher.username}</span>
            {/if}
          </div>
          <div class="info-item">
            <span class="label">Email:</span>
            {#if editMode}
              <input type="email" bind:value={editableData.teacher.email} class="edit-input" />
            {:else}
              <span class="value">{teacherDetails.teacher.email}</span>
            {/if}
          </div>
          <div class="info-item">
            <span class="label">Phone Number:</span>
            {#if editMode}
              <input type="text" bind:value={editableData.teacher.phone_number} class="edit-input" placeholder="+370 600 12345" />
            {:else}
              <span class="value">{teacherDetails.teacher.phone_number || 'Not set'}</span>
            {/if}
          </div>
          <div class="info-item">
            <span class="label">Google Calendar ID:</span>
            {#if editMode}
              <input type="text" bind:value={editableData.teacher.google_calendar_id} class="edit-input" />
            {:else}
              <span class="value">{teacherDetails.teacher.google_calendar_id}</span>
            {/if}
          </div>
          <div class="info-item">
            <span class="label">Bank Number:</span>
            {#if editMode}
              <input type="text" bind:value={editableData.teacher.bank_nr} class="edit-input" />
            {:else}
              <span class="value">{teacherDetails.teacher.bank_nr}</span>
            {/if}
          </div>
          <div class="info-item">
            <span class="label">Individual License:</span>
            {#if editMode}
              <input type="text" name="admin-individual-activity-certificate" inputmode="numeric" autocomplete="off" autocapitalize="none" autocorrect="off" spellcheck="false" data-lpignore="true" data-1p-ignore="true" bind:value={editableData.teacher.indiv_lic} class="edit-input" />
            {:else}
              <span class="value">{teacherDetails.teacher.indiv_lic}</span>
            {/if}
          </div>
          <div class="info-item">
            <span class="label">Meeting Link:</span>
            {#if editMode}
              <input type="url" bind:value={editableData.teacher.meeting_link} class="edit-input" placeholder="https://zoom.us/j/..." />
            {:else}
              <span class="value">{teacherDetails.teacher.meeting_link || 'Not set'}</span>
            {/if}
          </div>
          <div class="info-item qr-code-admin-item">
            <span class="label">Teisėto darbo su vaikais QR kodas:</span>
            {#if teacherDetails.teacher.work_with_children_qr_code?.exists}
              <span class="value">Uploaded {new Date(teacherDetails.teacher.work_with_children_qr_code.uploaded_at).toLocaleString('lt-LT')}</span>
              <button class="btn btn-secondary btn-small" type="button" on:click={viewTeacherQrCode} disabled={qrCodeLoading}>
                {qrCodeLoading ? 'Loading...' : 'View QR code'}
              </button>
            {:else}
              <span class="value">Not uploaded</span>
            {/if}
          </div>
        </div>
        {#if qrCodeError}
          <div class="qr-code-admin-error" role="alert">{qrCodeError}</div>
        {/if}
        {#if qrCodePreviewUrl}
          <div class="qr-code-admin-preview">
            <img src={qrCodePreviewUrl} alt="Teisėto darbo su vaikais QR kodas" />
            <button class="btn btn-secondary btn-small" type="button" on:click={closeQrCodePreview}>Close QR code</button>
          </div>
        {/if}
      </div>

      <!-- Teaching Information -->
      <div class="info-section">
        <h5>Teaching Information</h5>
        
        {#if editMode}
          <!-- Edit mode - enhanced subject selection -->
          <div class="info-grid">
            <div class="info-item-full-width">
              <span class="label">Subjects:</span>
              
              {#if editableSubjects && editableSubjects.length > 0}
                <div class="selected-subjects">
                  <div>Selected subjects:</div>
                  {#each editableSubjects as subject}
                    <span class="selected-subject-tag">
                      {subject}
                      <span 
                        class="remove-subject" 
                        on:click={() => removeSubject(subject)}
                        on:keydown={(e) => e.key === 'Enter' && removeSubject(subject)}
                        tabindex="0"
                        role="button"
                        aria-label="Remove {subject}"
                      >×</span>
                    </span>
                  {/each}
                </div>
              {/if}

              <div class="subjects-container">
                <div style="margin-bottom: 8px; font-size: 13px; color: #888; font-style: italic;">Format: subject + language (e.g., "Lietuvių k. Angl." = Lithuanian language, taught in English)</div>
                <div style="margin-bottom: 12px; font-size: 14px; color: #666;">Select subjects:</div>
                {#each availableSubjects as subject}
                  <div class="subject-item">
                    <label class="subject-label">
                      <input 
                        type="checkbox" 
                        checked={editableSubjects && editableSubjects.includes(subject)}
                        on:change={() => toggleSubject(subject)}
                      >
                      <span class="checkmark"></span>
                      <span class="subject-text">{subject}</span>
                    </label>
                  </div>
                {/each}
              </div>
            </div>
            <div class="info-item">
              <span class="label">Grade Range:</span>
              <input type="text" bind:value={editableData.teacher_info.grade} class="edit-input" placeholder="e.g., 1-6 or 10-12" />
            </div>
          </div>
        {:else}
          <!-- Display mode - show detailed structure -->
          {#if Array.isArray(teacherDetails.teacher_info) && teacherDetails.teacher_info.length > 0}
            <div class="teacher-info-detailed">
              <p class="info-description">Subject & Grade Combinations:</p>
              {#each teacherDetails.teacher_info as info, index}
                <div class="subject-grade-combo">
                  <div class="combo-header">
                    <span class="combo-title">Combination {index + 1}</span>
                  </div>
                  <div class="combo-details">
                    <div class="combo-item">
                      <span class="combo-label">Subjects:</span>
                      <span class="combo-value">
                        {info.subjects.length > 0 ? info.subjects.join(', ') : 'No subjects'}
                      </span>
                    </div>
                    <div class="combo-item">
                      <span class="combo-label">Grade Range:</span>
                      <span class="combo-value">{info.grade || 'Not specified'}</span>
                    </div>
                  </div>
                </div>
              {/each}
            </div>
          {:else}
            <!-- Fallback to legacy format if new format not available -->
            <div class="info-grid">
              <div class="info-item">
                <span class="label">Subjects:</span>
                <span class="value">
                  {(teacherDetails.teacher_info_legacy?.subjects?.length > 0 || teacherDetails.teacher_info?.subjects?.length > 0)
                    ? (teacherDetails.teacher_info_legacy?.subjects || teacherDetails.teacher_info?.subjects).join(', ') 
                    : 'No subjects assigned'}
                </span>
              </div>
              <div class="info-item">
                <span class="label">Grade Range:</span>
                <span class="value">
                  {teacherDetails.teacher_info_legacy?.grade || teacherDetails.teacher_info?.grade || 'No grade range specified'}
                </span>
              </div>
            </div>
          {/if}

          <div class="info-item student-count-item">
            <span class="label">Kiek norite pamokų per <strong>savaitę</strong>?</span>
            <span class="value">
              {(teacherDetails.teacher?.desired_student_count ?? null) !== null
                ? teacherDetails.teacher.desired_student_count
                : 'Nenurodyta'}
            </span>
          </div>
        {/if}
      </div>

      <!-- Time Slots -->
      <div class="info-section">
        <div class="timeslots-header">
          <h5>Available Time Slots</h5>
          {#if editMode}
            <button class="btn btn-small" on:click={addTimeSlot}>
              Add Time Slot
            </button>
          {/if}
        </div>
        
        {#if editMode}
          {#if editableData.timeslots.length > 0}
            <div class="timeslots-edit">
              {#each editableData.timeslots as slot, index}
                <div class="timeslot-edit-item">
                  <select bind:value={slot.day_of_week} on:change={(e) => updateDayName(index, e.target.value)} class="day-select">
                    <option value={1}>Monday</option>
                    <option value={2}>Tuesday</option>
                    <option value={3}>Wednesday</option>
                    <option value={4}>Thursday</option>
                    <option value={5}>Friday</option>
                    <option value={6}>Saturday</option>
                    <option value={7}>Sunday</option>
                  </select>
                  <input type="time" bind:value={slot.start_time} class="time-input" />
                  <input type="time" bind:value={slot.end_time} class="time-input" />
                  <button class="btn btn-danger btn-small" on:click={() => removeTimeSlot(index)}>
                    Remove
                  </button>
                </div>
              {/each}
            </div>
          {:else}
            <p class="no-data">No time slots defined. Click "Add Time Slot" to add one.</p>
          {/if}
        {:else}
          {#if teacherDetails.timeslots.length > 0}
            <div class="timeslots-grid">
              {#each teacherDetails.timeslots as slot}
                <div class="timeslot-item">
                  <span class="day">{slot.day_name}</span>
                  <span class="time">{slot.start_time} - {slot.end_time}</span>
                </div>
              {/each}
            </div>
          {:else}
            <p class="no-data">No time slots defined for this teacher</p>
          {/if}
        {/if}
      </div>
    </div>
  {/if}
</div>

<style>
  .teacher-info-container {
    padding: 20px;
    max-width: 1200px;
    margin: 0 auto;
  }

  .teacher-info-container.embedded {
    padding: 0;
    max-width: none;
    margin: 0;
  }

  .selection-section {
    background-color: #f8f9fa;
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
  }

  .selection-section h4 {
    margin: 0 0 15px 0;
    color: #333;
    font-size: 1.1em;
  }

  .month-selection {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
  }

  .month-selector-container {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .selector-label {
    font-weight: 600;
    color: #555;
    font-size: 0.95em;
  }

  .month-select,
  .year-select {
    padding: 8px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.9em;
    background-color: white;
    cursor: pointer;
    transition: border-color 0.2s;
  }

  .month-select {
    min-width: 130px;
  }

  .year-select {
    min-width: 90px;
  }

  .month-select:focus,
  .year-select:focus {
    outline: none;
    border-color: #fab45a;
    box-shadow: 0 0 0 2px rgba(250, 180, 90, 0.2);
  }

  .month-select:hover,
  .year-select:hover {
    border-color: #fab45a;
  }

  .selected-period {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background-color: #fff3e0;
    border: 2px solid #fab45a;
    border-radius: 6px;
    margin-left: auto;
  }

  .period-label {
    font-weight: 500;
    color: #666;
    font-size: 0.9em;
  }

  .period-value {
    font-weight: 600;
    color: #fab45a;
    font-size: 1em;
  }

  .input-container {
    position: relative;
    margin-top: 15px;
  }

  .search-input {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 0.9em;
    background-color: white;
  }

  .search-input:focus {
    outline: none;
    border-color: #fab45a;
    box-shadow: 0 0 0 2px rgba(250, 180, 90, 0.2);
  }

  .autocomplete-list {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #ddd;
    border-top: none;
    border-radius: 0 0 6px 6px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 1000;
    list-style: none;
    padding: 0;
    margin: 0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  }

  .autocomplete-list li {
    border-bottom: 1px solid #f0f0f0;
  }

  .autocomplete-list li:last-child {
    border-bottom: none;
  }

  .autocomplete-list button {
    width: 100%;
    padding: 12px;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;
    transition: background-color 0.2s;
  }

  .autocomplete-list button:hover {
    background-color: #f8f9fa;
  }

  .teacher-option {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .teacher-name {
    font-weight: 500;
    color: #333;
    font-size: 0.9em;
  }

  .teacher-email {
    color: #666;
    font-size: 0.8em;
  }

  .selected-teacher {
    margin-top: 20px;
    padding: 15px;
    background-color: #fff3e0;
    border: 2px solid #fab45a;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 15px;
  }

  .selected-teacher-info h5 {
    margin: 0 0 10px 0;
    color: #fab45a;
    font-size: 1em;
  }

  .selected-teacher-info p {
    margin: 5px 0;
    font-size: 0.9em;
  }

  .selected-teacher-info p strong {
    color: #333;
  }

  .btn-clear-small {
    padding: 8px 16px;
    background-color: #6c757d;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.85em;
    white-space: nowrap;
  }

  .btn-clear-small:hover {
    background-color: #545b62;
  }

  .teacher-info-container h3 {
    margin: 0 0 20px 0;
    color: #333;
    border-bottom: 2px solid #fab45a;
    padding-bottom: 10px;
  }

  .error-message {
    background-color: #f8d7da;
    color: #721c24;
    padding: 10px;
    border: 1px solid #f5c6cb;
    border-radius: 5px;
    margin-bottom: 20px;
  }

  .loading {
    text-align: center;
    padding: 20px;
    color: #666;
    font-style: italic;
  }

  .teacher-overview {
    background: #fff;
    border: 2px solid #fab45a;
    border-radius: 8px;
    padding: 20px;
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .teacher-overview h4 {
    margin: 0 0 20px 0;
    color: #fab45a;
    font-size: 1.2em;
    border-bottom: 2px solid #fab45a;
    padding-bottom: 10px;
  }

  .overview-section {
    margin-bottom: 20px;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 6px;
  }

  .overview-section h5 {
    margin: 0 0 15px 0;
    color: #333;
    font-size: 1.1em;
  }

  .overview-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 15px;
  }

  .overview-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    background: white;
    border-radius: 4px;
    border-left: 4px solid #fab45a;
  }

  .overview-label {
    font-weight: 600;
    color: #555;
  }

  .overview-value {
    font-weight: bold;
    color: #fab45a;
  }

  .subjects-overview {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }

  .subject-badge {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 10px;
    background: #fab45a;
    color: black;
    border-radius: 6px;
    min-width: 100px;
  }

  .subject-name {
    font-weight: bold;
    font-size: 0.9em;
  }

  .subject-count {
    font-size: 0.8em;
    opacity: 0.8;
  }

  .students-overview {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 10px;
  }

  .student-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background: white;
    border-radius: 4px;
    border: 1px solid #ddd;
  }

  .student-name {
    font-weight: 500;
    color: #333;
  }

  .student-details {
    color: #666;
    font-size: 0.8em;
  }

  .available-slots {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .slot-item {
    display: grid;
    grid-template-columns: 100px 140px 1fr auto;
    align-items: center;
    padding: 10px;
    border-radius: 4px;
    gap: 10px;
  }

  .slot-item.available {
    background: #d4edda;
    border: 1px solid #c3e6cb;
  }

  .slot-day {
    font-weight: 600;
    color: #333;
  }

  .slot-time {
    font-family: monospace;
    color: #555;
  }

  .slot-status {
    color: #155724;
    font-weight: 500;
  }

  .no-slots {
    color: #666;
    font-style: italic;
    margin: 0;
    text-align: center;
    padding: 20px;
  }

  /* Teacher Comments Styles */
  .teacher-comments {
    background: #f8f9fa;
    border: 2px solid #fab45a;
    border-radius: 8px;
    padding: 20px;
    margin-top: 20px;
    margin-bottom: 20px;
  }

  .teacher-comments h4 {
    margin: 0 0 15px 0;
    color: #fab45a;
    font-size: 1.2em;
    border-bottom: 2px solid #fab45a;
    padding-bottom: 10px;
  }

  .comments-summary {
    margin-bottom: 20px;
    padding: 15px;
    background: white;
    border-radius: 6px;
    border-left: 4px solid #fab45a;
  }

  .summary-header {
    font-size: 1.1em;
    color: #fab45a;
    margin-bottom: 5px;
  }

  .comments-summary p {
    margin: 0;
    color: #666;
    font-size: 0.9em;
  }

  .comments-section {
    margin-bottom: 20px;
    padding: 15px;
    background: white;
    border-radius: 6px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
  }

  .comments-section h5 {
    margin: 0 0 15px 0;
    color: #333;
    font-size: 1.1em;
    border-bottom: 1px solid #eee;
    padding-bottom: 8px;
  }

  .month-comparison {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
  }

  .month-data {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 6px;
    border-left: 4px solid #fab45a;
  }

  .month-data h6 {
    margin: 0 0 12px 0;
    color: #fab45a;
    font-size: 1em;
    font-weight: 600;
  }

  .month-data ul {
    margin: 0;
    padding-left: 15px;
  }

  .month-data li {
    margin-bottom: 8px;
    color: #555;
    font-size: 0.9em;
  }

  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 15px;
  }

  .metric-item {
    background: #f8f9fa;
    padding: 15px;
    border-radius: 6px;
    border-left: 4px solid #fab45a;
  }

  .metric-item strong {
    display: block;
    margin-bottom: 10px;
    color: #333;
    font-size: 0.95em;
  }

  .metric-bar {
    background: #e9ecef;
    border-radius: 10px;
    height: 8px;
    margin-bottom: 8px;
    overflow: hidden;
  }

  .metric-fill {
    background: linear-gradient(90deg, #fab45a, #e09a43);
    height: 100%;
    transition: width 0.3s ease;
  }

  .metric-item small {
    color: #666;
    font-size: 0.8em;
  }

  .sample-comments {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .comment-item {
    padding: 12px;
    background: #f8f9fa;
    border-radius: 6px;
    border-left: 4px solid #fab45a;
  }

  .comment-item strong {
    color: #fab45a;
    font-size: 0.9em;
  }

  .comment-item span {
    color: #555;
    font-style: italic;
    margin-left: 8px;
  }

  .week-classes {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .class-item {
    display: grid;
    grid-template-columns: 200px 1fr auto;
    align-items: center;
    padding: 12px;
    border-radius: 6px;
    gap: 15px;
    border: 2px solid transparent;
  }

  .class-item.in-timeslot {
    background: #d1ecf1;
    border-color: #17a2b8;
  }

  .class-item.outside-timeslot {
    background: #f8d7da;
    border-color: #dc3545;
  }

  .class-time {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .class-day {
    font-weight: 600;
    color: #333;
    font-size: 0.9em;
  }

  .class-datetime {
    font-family: monospace;
    color: #555;
    font-size: 0.85em;
  }

  .class-details {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .class-name {
    font-weight: 500;
    color: #333;
    font-size: 0.95em;
  }

  .class-student {
    color: #666;
    font-size: 0.85em;
  }

  .timeslot-indicator {
    background: #17a2b8;
    color: white;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 0.75em;
    font-weight: 500;
    white-space: nowrap;
  }

  .outside-indicator {
    background: #dc3545;
    color: white;
    padding: 4px 8px;
    border-radius: 12px;
    font-size: 0.75em;
    font-weight: 500;
    white-space: nowrap;
  }

  /* Teaching Start Date styles */
  .teaching-start-info {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .start-date-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px;
    background: white;
    border-radius: 6px;
    border-left: 4px solid #fab45a;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  .start-date-label {
    font-weight: 600;
    color: #555;
  }

  .start-date-value {
    font-weight: bold;
    color: #fab45a;
    font-size: 1.05em;
  }

  .no-start-date {
    color: #666;
    font-style: italic;
    margin: 0;
    text-align: center;
    padding: 20px;
  }

  .teacher-details {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 20px;
    margin-top: 20px;
  }

  .teacher-details-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    flex-wrap: wrap;
    gap: 10px;
  }

  .teacher-details h4 {
    margin: 0;
    color: #333;
    font-size: 1.2em;
  }

  .edit-controls {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
  }

  .btn {
    padding: 8px 16px;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    font-size: 0.9em;
    font-weight: 500;
    transition: all 0.2s;
    text-decoration: none;
    display: inline-block;
  }

  .btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  .btn-primary {
    background-color: #fab45a;
    color: black;
  }

  .btn-primary:hover:not(:disabled) {
    background-color: #e09a43;
  }

  .btn-secondary {
    background-color: #6c757d;
    color: white;
  }

  .btn-secondary:hover:not(:disabled) {
    background-color: #545b62;
  }

  .btn-danger {
    background-color: #dc3545;
    color: white;
  }

  .btn-danger:hover:not(:disabled) {
    background-color: #c82333;
  }

  .btn-small {
    padding: 6px 12px;
    font-size: 0.8em;
  }

  .edit-input {
    flex: 1;
    padding: 6px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9em;
  }

  .edit-input:focus {
    outline: none;
    border-color: #fab45a;
    box-shadow: 0 0 0 2px rgba(250, 180, 90, 0.2);
  }

  .info-section {
    margin-bottom: 25px;
    background: white;
    padding: 15px;
    border-radius: 6px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  }

  .info-section h5 {
    margin: 0 0 15px 0;
    color: #fab45a;
    font-size: 1.1em;
    border-bottom: 1px solid #eee;
    padding-bottom: 5px;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 15px;
  }

  .info-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
  }

  .info-item:last-child {
    border-bottom: none;
  }

  .qr-code-admin-item {
    flex-wrap: wrap;
    gap: 10px;
  }

  .qr-code-admin-preview {
    margin-top: 16px;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    background: #f8f9fa;
  }

  .qr-code-admin-preview img {
    display: block;
    max-width: 100%;
    max-height: 600px;
    margin: 0 auto 12px;
    object-fit: contain;
  }

  .qr-code-admin-error {
    margin-top: 12px;
    color: #c0392b;
    font-weight: 600;
  }

  .label {
    font-weight: 600;
    color: #555;
    min-width: 140px;
  }

  .value {
    color: #333;
    text-align: right;
    flex: 1;
    word-break: break-word;
  }

  .timeslots-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 10px;
  }

  .timeslot-item {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 5px;
    padding: 10px;
    text-align: center;
  }

  .day {
    display: block;
    font-weight: 600;
    color: #fab45a;
    margin-bottom: 5px;
  }

  .time {
    display: block;
    color: #666;
    font-size: 0.9em;
  }

  .no-data {
    color: #666;
    font-style: italic;
    margin: 0;
  }


  .timeslots-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }

  .timeslots-edit {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .timeslot-edit-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px;
    background: #f8f9fa;
    border-radius: 6px;
    flex-wrap: wrap;
  }

  .day-select {
    padding: 6px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9em;
    min-width: 120px;
  }

  .time-input {
    padding: 6px 10px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9em;
    min-width: 100px;
  }

  .day-select:focus,
  .time-input:focus {
    outline: none;
    border-color: #fab45a;
    box-shadow: 0 0 0 2px rgba(250, 180, 90, 0.2);
  }

  /* New styles for detailed teacher info */
  .teacher-info-detailed {
    display: flex;
    flex-direction: column;
    gap: 15px;
  }

  .info-description {
    margin: 0 0 10px 0;
    font-weight: 600;
    color: #555;
    font-size: 0.95em;
  }

  .subject-grade-combo {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 15px;
    border-left: 4px solid #fab45a;
  }

  .combo-header {
    margin-bottom: 10px;
  }

  .combo-title {
    font-weight: 600;
    color: #fab45a;
    font-size: 0.9em;
  }

  .combo-details {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .combo-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 0;
  }

  .combo-label {
    font-weight: 500;
    color: #666;
    min-width: 100px;
  }

  .combo-value {
    color: #333;
    text-align: right;
    flex: 1;
    word-break: break-word;
  }

  .student-count-item {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid #eee;
  }

  /* Subject editing styles adapted from AccountPage */
  .info-item-full-width {
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
  }

  .subjects-container {
    max-height: 180px;
    overflow-y: auto;
    overflow-x: hidden;
    border: 1px solid #ddd;
    border-radius: 8px;
    padding: 12px;
    background-color: #f8f9fa;
    margin-top: 8px;
    overscroll-behavior: contain;
    touch-action: pan-y;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: thin;
    user-select: none;
  }

  .subject-item {
    margin-bottom: 6px;
    background-color: white;
    border-radius: 6px;
    border: 1px solid #f0f0f0;
    transition: background-color 0.2s;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
  }

  .subject-item:hover {
    background-color: #f5f5f5;
    border-color: #e0e0e0;
  }

  .subject-label {
    display: flex;
    align-items: center;
    padding: 8px;
    cursor: pointer;
    width: 100%;
    position: relative;
  }

  .subject-label input[type="checkbox"] {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }

  .checkmark {
    height: 24px;
    width: 24px;
    background-color: white;
    border: 2px solid #333;
    border-radius: 6px;
    margin-right: 12px;
    position: relative;
    transition: all 0.3s ease;
    flex-shrink: 0;
  }

  .subject-label input:checked ~ .checkmark {
    background-color: #fab45a;
    border-color: #fab45a;
  }

  .subject-label input:checked ~ .checkmark:after {
    content: "";
    position: absolute;
    display: block;
    left: 6px;
    top: 2px;
    width: 6px;
    height: 12px;
    border: solid white;
    border-width: 0 3px 3px 0;
    transform: rotate(45deg);
  }

  .subject-text {
    font-size: 15px;
    color: #444;
    user-select: none;
  }

  .selected-subjects {
    margin-top: 12px;
    margin-bottom: 12px;
  }
  
  .selected-subjects > div {
    font-size: 14px;
    font-weight: 500;
    color: #666;
    margin-bottom: 8px;
  }

  .selected-subject-tag {
    display: inline-block;
    background-color: #fab45a;
    color: black;
    padding: 6px 12px;
    margin: 4px 4px 4px 0;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 400;
  }

  .remove-subject {
    margin-left: 8px;
    cursor: pointer;
    font-weight: bold;
    padding: 2px 4px;
    border-radius: 50%;
    transition: background-color 0.2s;
  }

  .remove-subject:hover {
    background-color: rgba(255, 255, 255, 0.3);
  }

  @media (max-width: 768px) {
    .teacher-info-container {
      padding: 15px;
    }

    .month-selection {
      flex-direction: column;
      align-items: stretch;
      gap: 15px;
    }

    .month-selector-container {
      flex-direction: column;
      align-items: stretch;
      gap: 8px;
    }

    .month-select,
    .year-select {
      width: 100%;
    }

    .selected-period {
      margin-left: 0;
      justify-content: center;
    }

    .combo-item {
      flex-direction: column;
      align-items: flex-start;
      gap: 5px;
    }

    .combo-label {
      min-width: auto;
    }

    .combo-value {
      text-align: left;
    }

    .overview-grid {
      grid-template-columns: 1fr;
    }

    .overview-item {
      flex-direction: column;
      align-items: flex-start;
      gap: 5px;
    }

    .subjects-overview {
      justify-content: center;
    }

    .students-overview {
      grid-template-columns: 1fr;
    }

    .slot-item {
      grid-template-columns: 1fr;
      gap: 5px;
      text-align: center;
    }

    .class-item {
      grid-template-columns: 1fr;
      gap: 10px;
      text-align: left;
    }

    .class-time {
      text-align: center;
    }

    .info-grid {
      grid-template-columns: 1fr;
    }

    .info-item {
      flex-direction: column;
      align-items: flex-start;
      gap: 5px;
    }

    .label {
      min-width: auto;
    }

    .value {
      text-align: left;
    }

    .timeslots-grid {
      grid-template-columns: 1fr;
    }

    .month-comparison {
      grid-template-columns: 1fr;
    }

    .metrics-grid {
      grid-template-columns: 1fr;
    }

    .start-date-item {
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
    }

    .start-date-label {
      font-size: 0.9em;
    }

    .start-date-value {
      text-align: left;
      font-size: 1em;
    }

    /* Mobile styles for subject editing */
    .subjects-container {
      max-height: 150px;
    }
    
    .subject-label {
      padding: 12px;
    }
    
    .checkmark {
      height: 28px;
      width: 28px;
      margin-right: 15px;
    }
    
    .subject-label input:checked ~ .checkmark:after {
      left: 8px;
      top: 3px;
      width: 7px;
      height: 14px;
    }
    
    .subject-text {
      font-size: 16px;
    }
  }
</style>

